/**
 * Header Component
 * 
 * Displays application branding, user info, and logout button
 * Responsive design for mobile and desktop
 * 
 * Requirements: 10.7, 10.1, 10.2
 */

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { Button } from '../common';

interface HeaderProps {
  className?: string;
}

export function Header({ className = '' }: HeaderProps) {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();

  return (
    <header
      className={`bg-primary-500 text-white ${className}`}
      role="banner"
    >
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Branding - Requirements: 10.7 */}
          <div className="flex items-center space-x-3">
            <h1 className="text-xl md:text-2xl font-bold">
              Blue Clipboard
            </h1>
          </div>

          {/* User Info and Actions - Requirements: 10.1, 10.2 */}
          {isAuthenticated && user && (
            <div className="flex items-center space-x-2 md:space-x-4">
              {/* User Info - Hidden on small screens */}
              <div className="hidden sm:flex flex-col items-end text-sm">
                <span className="font-medium">{user.name || user.given_name || user.email}</span>
                {user.groups && user.groups.length > 0 && (
                  <span className="text-xs text-primary-100">
                    {user.groups.includes('admin')
                      ? 'Admin'
                      : user.groups.includes('coach')
                      ? 'Coach'
                      : 'User'}
                  </span>
                )}
              </div>

              {/* Profile Button */}
              <button
                onClick={() => navigate('/profile')}
                className="text-white hover:text-primary-100 transition-colors p-2"
                aria-label="View Profile"
                title="Profile"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </button>

              {/* Logout Button */}
              <Button
                variant="secondary"
                onClick={logout}
                className="bg-white text-primary-500 hover:bg-primary-50 px-3 py-1.5 text-sm"
                aria-label="Logout"
              >
                <span className="hidden sm:inline">Logout</span>
                <span className="sm:hidden">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                </span>
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
